import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, SearchForm, Button } from 'shared/components';
import SuccessIcon from 'assets/svgs/successIcon.svg';
import BlueExclamationCircle from 'assets/svgs/blueExclamationCircle.svg';
import ErrorIcon from 'assets/svgs/errorIcon.svg';
import { ValidationService } from 'shared/services';
import { fetchInvitations } from './StaffPoolModal';
import PropTypes from 'prop-types';
import StaffService from '../StaffService';
import { errorStrings, validationRules } from 'shared/validations';

const ButtonContainer = (props) => {
  const { t } = useTranslation();
  const { handleCancel, handleInvite } = props;
  return (
    <div className="button-container">
      <Button
        onClick={handleCancel}
        type="btn-outline"
        className="btn-small"
        text={t('common.labels.cancel')}
      />
      <Button
        onClick={handleInvite}
        type="btn-primary"
        className="btn-small"
        text={t('staffAndRoomsManagement.staffPool.sendInvite')}
      />
    </div>
  );
};

const InitialStatus = ({
  id,
  currentUserId,
  invitedStaff,
  setInvitedStaff,
  setAlert,
  staffActions,
  tcEvent,
  name,
}) => {
  const { t } = useTranslation();
  const statusElements = {
    initial: 'initial',
    personnelFound: 'personnelFound',
    personnelNotFound: 'personnelNotFound',
  };
  const [isMember, setIsMember] = useState(false);
  const [status, setStatus] = useState(statusElements.initial);
  const [personnel, setPersonnel] = useState({});
  const [searchValue, setSearchValue] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [errors, setErrors] = useState({});
  const [emailError, setEmailError] = useState('');

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setEmailError('');
  };
  const handleSearch = async () => {
    setIsMember(false);
    setPersonnel({});
    const isValidEmail = ValidationService.validEmail(searchValue);
    if (!searchValue.length) {
      setEmailError(t('staffAndRoomsManagement.staffPool.noEmail'));
    } else {
      if (isValidEmail) {
        setEmailError('');
        setFirstName('');
        setLastName('');
        const staffFound = invitedStaff.find(
          (item) => item.email.toLowerCase() === searchValue.toLowerCase()
        );
        if (staffFound) {
          setPersonnel({
            firstName: staffFound.firstName,
            lastName: staffFound.lastName,
            emailAddress: searchValue,
          });
          setIsMember(true);
          setSearchValue('');
        } else {
          const personnelResponse = await StaffService.getPersonnelByEmail(searchValue);
          if (Object.keys(personnelResponse).length !== 0) {
            setStatus(statusElements.personnelFound);
            setPersonnel({
              firstName: personnelResponse.firstName,
              lastName: personnelResponse.lastName,
              emailAddress: personnelResponse.emailAddress,
              invitedByPersonnelId: currentUserId,
              testCenter: {
                id,
                name,
              },
            });
          } else {
            setStatus(statusElements.personnelNotFound);
          }
        }
      } else {
        setEmailError(t('errors.emailInvalid'));
      }
    }
  };
  const handleCancel = () => {
    setStatus(statusElements.initial);
    setSearchValue('');
    setErrors({});
  };
  const handleSentInvitations = async (data) => {
    try {
      await StaffService.invitePersonnel(data);
      const sentInvitations = await fetchInvitations(id);
      setInvitedStaff(sentInvitations);
      setStatus(statusElements.initial);
      setSearchValue('');
      setAlert({
        message: t('staffAndRoomsManagement.staffPool.sentSuccess'),
        isOpen: true,
        time: 3000,
      });
      staffActions.getInvitedEventStaff(tcEvent.id);
    } catch (err) {
      setAlert({
        message: t('staffAndRoomsManagement.staffPool.sentError'),
        isOpen: true,
        time: 3000,
        isError: true,
      });
    }
  };
  const handleInviteMember = async () => {
    setSearchValue('');
    await handleSentInvitations(personnel);
  };
  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };
  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };
  const handleInviteNonMember = async () => {
    setErrors({});
    const nameFieldErrors = ValidationService.validate(
      {
        firstName,
        lastName,
      },
      validationRules,
      errorStrings
    );
    if (Object.keys(nameFieldErrors).length) {
      setErrors(nameFieldErrors);
    } else {
      setSearchValue('');
      const data = {
        firstName,
        lastName,
        emailAddress: searchValue,
        invitedByPersonnelId: currentUserId,
        testCenter: {
          id,
          name,
        },
      };
      await handleSentInvitations(data);
    }
  };
  return (
    <div>
      {status === statusElements.initial && (
        <>
          {isMember && (
            <div className="initial-status">
              <div className="already-member">
                <ErrorIcon />
                {t('staffAndRoomsManagement.staffPool.alreadyInStaffPool')}
              </div>
              <div className="individual-name">
                {personnel.lastName}, {personnel.firstName}
              </div>
              <div className="individual-email">{personnel.emailAddress}</div>
            </div>
          )}
          <div className="invite-individual-title">
            {t('staffAndRoomsManagement.staffPool.inviteNewUsers')}
          </div>
          <div className="invite-individual-email">
            {t('staffAndRoomsManagement.notifyStaff.email')}
          </div>
          <div className="verify-email">
            <SearchForm
              className="individual-search"
              displayIconButton={false}
              customButton={t('common.labels.verify')}
              searchValue={searchValue}
              onSearch={handleSearch}
              onChange={handleSearchChange}
              errors={emailError}
            />
          </div>
          <div className="invite-individual-provide">
            {' '}
            {t('staffAndRoomsManagement.staffPool.provideEmail')}
          </div>
        </>
      )}
      {status === statusElements.personnelFound && (
        <div>
          <div className="already-member account-found">
            <SuccessIcon />
            <div>
              <div>{t('staffAndRoomsManagement.staffPool.accountFound')}</div>
              <div>{t('staffAndRoomsManagement.staffPool.selectSend')}</div>
            </div>
          </div>
          <div className="individual-name">
            {personnel.lastName}, {personnel.firstName}
          </div>
          <div className="individual-email">{personnel.emailAddress}</div>
          <ButtonContainer handleInvite={handleInviteMember} handleCancel={handleCancel} />
        </div>
      )}
      {status === statusElements.personnelNotFound && (
        <div className="not-found">
          <div className="already-member account-not-found">
            <BlueExclamationCircle />
            <div>
              <div>
                {t('staffAndRoomsManagement.staffPool.accountNotFound')}
                <span> {searchValue}</span>
              </div>
              <div className="not-found-description">
                {t('staffAndRoomsManagement.staffPool.notRegistered')}
              </div>
            </div>
          </div>
          <div className="form-container">
            <div>
              <Input
                type="text"
                name="lastName"
                label="common.labels.lastName"
                className="last-name"
                errors={errors.lastName}
                maxLength="25"
                onFocus={() => {
                  setErrors({
                    firstName: errors.firstName,
                  });
                }}
                required
                value={lastName}
                onChange={handleLastNameChange}
              />
              <Input
                type="text"
                name="firstName"
                label="common.labels.firstName"
                className="first-name"
                errors={errors.firstName}
                maxLength="25"
                onFocus={() => {
                  setErrors({
                    lastName: errors.lastName,
                  });
                }}
                required
                value={firstName}
                onChange={handleFirstNameChange}
              />
            </div>
            <ButtonContainer handleInvite={handleInviteNonMember} handleCancel={handleCancel} />
          </div>
        </div>
      )}
    </div>
  );
};

export default InitialStatus;

InitialStatus.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  currentUserId: PropTypes.number.isRequired,
  invitedStaff: PropTypes.arrayOf(
    PropTypes.shape({
      email: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      status: PropTypes.string,
      id: PropTypes.number.isRequired,
    })
  ).isRequired,
  setInvitedStaff: PropTypes.func.isRequired,
  setAlert: PropTypes.func.isRequired,
};
